import { createGate, useStore } from 'effector-react';
import { attach, createEffect, createEvent, createStore, sample } from 'effector';

import { AuthType } from '@/models';
import { Authentication } from '@/services/authentication.service';
import { AuthApi } from '@/services/api.service';

export type AuthState = {
  authorized: boolean;
  authChecked: boolean;
};

export const AuthProviderGate = createGate<Authentication>();

export const $signInErrorMessage = createStore('');
export const $authState = createStore<AuthState>({ authChecked: false, authorized: false });
export const $authProvider = createStore<Authentication | null>(null);

export const setSignInErrorMessage = createEvent<string>();
export const setAuthState = createEvent<Partial<AuthState>>();

export const signInFx = attach({
  source: { authProvider: $authProvider },
  mapParams: ({ body, authType }: { body: string; authType: AuthType }, { authProvider }) => ({
    authProvider,
    authType,
    body,
  }),
  effect: createEffect(
    async (params: { authProvider: Authentication | null; body: string; authType: AuthType }) => {
      const { body, authProvider, authType } = params;

      setSignInErrorMessage('');

      if (authType === 'default') {
        const { email, password } = JSON.parse(body);

        if (!email || !password) {
          setSignInErrorMessage('Login or password are empty');

          return null;
        }
      }

      const result = await AuthApi.signIn({
        authType,
        body,
        url: authProvider?.urls.signIn ?? '',
      });

      setAuthState({ authChecked: true });

      return result.match(
        (data) => {
          authProvider?.tokenInfo.saveAuthData({ ...data!, isRefreshing: false, authType });

          return data!;
        },
        () => null,
      );
    },
  ),
});

export const logoutFx = attach({
  source: { authProvider: $authProvider },
  mapParams: ({ authType }: { authType: AuthType }, { authProvider }) => ({
    authProvider,
    authType,
  }),
  effect: createEffect(
    async (params: { authProvider: Authentication | null; authType: AuthType }) => {
      const { authProvider, authType } = params;

      const result = await AuthApi.logout({
        url: authProvider?.urls.signOut ?? '',
        authType,
      });

      return result.match(
        (data) => data!,
        () => null,
      );
    },
  ),
});

export const refreshTokenFx = attach({
  source: { authProvider: $authProvider },
  mapParams: ({ token, authType }: { token: string; authType: AuthType }, { authProvider }) => ({
    authProvider,
    authType,
    token,
  }),
  effect: createEffect(
    async (params: { authProvider: Authentication | null; token: string; authType: AuthType }) => {
      const { token, authProvider, authType } = params;

      authProvider?.tokenInfo.setRefreshing(true);

      const result = await AuthApi.refreshToken({
        url: authProvider?.urls.refresh ?? '',
        token,
        authType,
      });

      setAuthState({ authChecked: true });

      return result.match(
        (data) => {
          authProvider?.tokenInfo.saveAuthData({ ...data!, isRefreshing: false, authType });

          return data!;
        },
        () => {
          authProvider?.tokenInfo.setRefreshing(false);

          return null;
        },
      );
    },
  ),
});

export const $isRefreshInProgress = refreshTokenFx.pending;
export const $isLoginInProgress = signInFx.pending;
export const $isLogoutInProgress = logoutFx.pending;

$authState.on(setAuthState, (state, payload) => ({
  ...state,
  ...payload,
}));

sample({
  clock: setSignInErrorMessage,
  target: $signInErrorMessage,
});

sample({
  source: AuthProviderGate.state,
  filter: (payload) => !!Object.entries(payload).length,
  target: $authProvider,
});

export const useAuthState = () => useStore($authState);
export const useAuthProvider = () => useStore($authProvider);
export const useErrorMessage = () => useStore($signInErrorMessage);

export const useIsLoginInProgress = () => useStore($isLoginInProgress);
export const useIsRefreshInProgress = () => useStore($isRefreshInProgress);
export const useIsLogoutInProgress = () => useStore($isLoginInProgress);
